import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, Edit, RefreshCw, UserX, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSignals() {
  const [signals, setSignals] = useState([]);
  const [videos, setVideos] = useState([]);
  const [resources, setResources] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('posts');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  
  // Signal Form
  const [signalForm, setSignalForm] = useState({
    title: '',
    signal_type: 'buy',
    symbol: '',
    entry_price: '',
    stop_loss: '',
    take_profit: '',
    take_profits: [''],
    description: '',
    chart_image_base64: '',
    chart_image_file: null,
    status: 'active',
    close_price: ''
  });

  // Video Form
  const [videoForm, setVideoForm] = useState({
    title: '', url: '', platform: 'youtube', category: 'basicos', description: '', order: 0
  });
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [videoCategoryFilter, setVideoCategoryFilter] = useState('all');

  // Resource Form
  const [resourceForm, setResourceForm] = useState({
    title: '',
    url: '',
    resource_type: 'pdf',
    description: '',
    uploadMode: 'url'
  });

  // Post Form
  const [postContent, setPostContent] = useState('');
  const [postImages, setPostImages] = useState([]);
  const [postUploading, setPostUploading] = useState(false);

  // Alert Form
  const [alertForm, setAlertForm] = useState({ title: '', description: '', impact: 'high', event_time: '' });

  // Poll Form
  const [pollForm, setPollForm] = useState({ question: '', options: ['', ''], allow_multiple: false });
  const [polls, setPolls] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [signalsRes, videosRes, resourcesRes, usersRes, postsRes, alertsRes, pollsRes] = await Promise.all([
        axios.get(`${API}/signals`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/users`),
        axios.get(`${API}/posts`),
        axios.get(`${API}/alerts`),
        axios.get(`${API}/polls`)
      ]);
      setSignals(signalsRes.data);
      setVideos(videosRes.data);
      setResources(resourcesRes.data);
      setUsers(usersRes.data);
      setPosts(postsRes.data);
      setAlerts(alertsRes.data);
      setPolls(pollsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = `${BACKEND_URL}${res.data.url}`;
        setSignalForm({ ...signalForm, chart_image_base64: imageUrl, chart_image_file: null });
        toast.success('Imagen subida correctamente');
      } catch (error) {
        toast.error('Error al subir imagen, se reintentará al crear la señal');
        // Store file reference for retry during signal creation, show local preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setSignalForm({ ...signalForm, chart_image_base64: reader.result, chart_image_file: file });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleResourceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const fileUrl = `${BACKEND_URL}${res.data.url}`;
        setResourceForm({ ...resourceForm, url: fileUrl });
        toast.success('Archivo subido correctamente');
      } catch (error) {
        toast.error('Error al subir archivo');
      }
    }
  };

  const createSignal = async () => {
    try {
      const tps = signalForm.take_profits.filter(tp => tp && tp.trim() !== '').map(tp => parseFloat(tp));
      
      // Upload image first if present
      let imageUrl = '';
      if (signalForm.chart_image_base64) {
        if (signalForm.chart_image_base64.startsWith('http')) {
          imageUrl = signalForm.chart_image_base64;
        } else if (signalForm.chart_image_file) {
          const formData = new FormData();
          formData.append('file', signalForm.chart_image_file);
          const uploadRes = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          imageUrl = uploadRes.data.url;
        }
      }
      
      const data = {
        title: signalForm.title,
        signal_type: signalForm.signal_type,
        symbol: signalForm.symbol,
        entry_price: parseFloat(signalForm.entry_price),
        stop_loss: signalForm.stop_loss ? parseFloat(signalForm.stop_loss) : null,
        take_profit: tps.length > 0 ? tps[0] : (signalForm.take_profit ? parseFloat(signalForm.take_profit) : null),
        take_profits: tps.length > 0 ? tps : null,
        description: signalForm.description,
        chart_image_base64: imageUrl || null,
      };
      await axios.post(`${API}/signals`, data);
      toast.success('Señal creada exitosamente');
      setDialogOpen(false);
      resetSignalForm();
      fetchAll();
    } catch (error) {
      console.error('Create signal error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al crear señal');
    }
  };

  const updateSignal = async () => {
    try {
      const tps = signalForm.take_profits.filter(tp => tp && tp.toString().trim() !== '').map(tp => parseFloat(tp));
      
      // Upload image if it's a base64 (not yet uploaded)
      let imageUrl = signalForm.chart_image_base64 || null;
      if (imageUrl && !imageUrl.startsWith('http') && signalForm.chart_image_file) {
        const formData = new FormData();
        formData.append('file', signalForm.chart_image_file);
        const uploadRes = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageUrl = `${BACKEND_URL}${uploadRes.data.url}`;
      }
      
      const data = {
        title: signalForm.title,
        signal_type: signalForm.signal_type,
        symbol: signalForm.symbol,
        entry_price: parseFloat(signalForm.entry_price),
        stop_loss: signalForm.stop_loss ? parseFloat(signalForm.stop_loss) : null,
        take_profit: tps.length > 0 ? tps[0] : (signalForm.take_profit ? parseFloat(signalForm.take_profit) : null),
        take_profits: tps.length > 0 ? tps : null,
        description: signalForm.description,
        status: signalForm.status,
        close_price: signalForm.close_price ? parseFloat(signalForm.close_price) : null
      };
      
      if (imageUrl) {
        data.chart_image_base64 = imageUrl;
      }
      
      await axios.put(`${API}/signals/${currentId}`, data);
      toast.success('Señal actualizada - Resultados calculados automáticamente');
      setDialogOpen(false);
      setEditMode(false);
      resetSignalForm();
      fetchAll();
    } catch (error) {
      console.error('Update error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al actualizar señal');
    }
  };

  const deleteSignal = async (id) => {
    if (!confirm('¿Eliminar esta señal?')) return;
    try {
      await axios.delete(`${API}/signals/${id}`);
      toast.success('Señal eliminada');
      fetchAll();
    } catch (error) {
      toast.error('Error al eliminar señal');
    }
  };

  const createVideo = async () => {
    try {
      if (editingVideoId) {
        await axios.put(`${API}/videos/${editingVideoId}`, videoForm);
        toast.success('Video actualizado');
      } else {
        await axios.post(`${API}/videos`, videoForm);
        toast.success('Video agregado');
      }
      setDialogOpen(false);
      resetVideoForm();
      fetchAll();
    } catch (error) {
      toast.error('Error al guardar video');
    }
  };

  const deleteVideo = async (id) => {
    if (!confirm('¿Eliminar este video?')) return;
    try {
      await axios.delete(`${API}/videos/${id}`);
      toast.success('Video eliminado');
      fetchAll();
    } catch (error) {
      toast.error('Error al eliminar video');
    }
  };

  const startEditVideo = (video) => {
    setVideoForm({
      title: video.title, url: video.url, platform: video.platform,
      category: video.category || 'basicos', description: video.description || '', order: video.order || 0
    });
    setEditingVideoId(video.id);
    setDialogOpen(true);
  };

  const moveVideo = async (video, direction) => {
    const catVideos = videos
      .filter(v => v.category === video.category)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = catVideos.findIndex(v => v.id === video.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= catVideos.length) return;
    const other = catVideos[swapIdx];
    try {
      await Promise.all([
        axios.put(`${API}/videos/${video.id}`, { ...video, order: other.order ?? swapIdx }),
        axios.put(`${API}/videos/${other.id}`, { ...other, order: video.order ?? idx }),
      ]);
      fetchAll();
    } catch { toast.error('Error al reordenar'); }
  };

  // Post handlers
  const handlePostImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setPostUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(`${BACKEND_URL}${res.data.url}`);
      } catch { toast.error(`Error al subir ${file.name}`); }
    }
    setPostImages([...postImages, ...uploaded]);
    setPostUploading(false);
  };

  const createPost = async () => {
    if (!postContent.trim()) return;
    try {
      await axios.post(`${API}/posts`, { content: postContent, images: postImages });
      toast.success('Publicación creada');
      setPostContent('');
      setPostImages([]);
      fetchAll();
    } catch { toast.error('Error al publicar'); }
  };

  const deletePost = async (id) => {
    if (!confirm('¿Eliminar esta publicación?')) return;
    try {
      await axios.delete(`${API}/posts/${id}`);
      toast.success('Publicación eliminada');
      fetchAll();
    } catch { toast.error('Error al eliminar'); }
  };

  const createAlert = async () => {
    if (!alertForm.title || !alertForm.event_time) return;
    try {
      const eventTimeWithTz = alertForm.event_time + '-05:00';
      await axios.post(`${API}/alerts`, { ...alertForm, event_time: eventTimeWithTz });
      toast.success('Alerta creada');
      setAlertForm({ title: '', description: '', impact: 'high', event_time: '' });
      fetchAll();
    } catch { toast.error('Error al crear alerta'); }
  };

  const createPoll = async () => {
    const validOpts = pollForm.options.filter(o => o.trim());
    if (!pollForm.question.trim() || validOpts.length < 2) {
      toast.error('Escribe una pregunta y al menos 2 opciones');
      return;
    }
    try {
      await axios.post(`${API}/polls`, { question: pollForm.question, options: validOpts, allow_multiple: pollForm.allow_multiple });
      toast.success('Encuesta creada');
      setPollForm({ question: '', options: ['', ''], allow_multiple: false });
      fetchAll();
    } catch { toast.error('Error al crear encuesta'); }
  };

  const deletePoll = async (id) => {
    if (!confirm('¿Eliminar esta encuesta?')) return;
    try {
      await axios.delete(`${API}/polls/${id}`);
      toast.success('Encuesta eliminada');
      fetchAll();
    } catch { toast.error('Error al eliminar'); }
  };

  const deleteAlert = async (id) => {
    if (!confirm('¿Eliminar esta alerta?')) return;
    try {
      await axios.delete(`${API}/alerts/${id}`);
      toast.success('Alerta eliminada');
      fetchAll();
    } catch { toast.error('Error al eliminar'); }
  };

  const createResource = async () => {
    try {
      await axios.post(`${API}/resources`, resourceForm);
      toast.success('Recurso agregado');
      setDialogOpen(false);
      resetResourceForm();
      fetchAll();
    } catch (error) {
      toast.error('Error al agregar recurso');
    }
  };

  const deleteResource = async (id) => {
    if (!confirm('¿Eliminar este recurso?')) return;
    try {
      await axios.delete(`${API}/resources/${id}`);
      toast.success('Recurso eliminado');
      fetchAll();
    } catch (error) {
      toast.error('Error al eliminar recurso');
    }
  };

  const toggleUserAccess = async (userId, currentStatus) => {
    const action = currentStatus ? 'desactivar' : 'activar';
    if (!confirm(`¿Seguro que quieres ${action} esta cuenta?`)) return;
    
    try {
      await axios.put(`${API}/users/${userId}`, { active: !currentStatus });
      toast.success(`Usuario ${action === 'activar' ? 'activado' : 'desactivado'} exitosamente`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al ${action} usuario`);
    }
  };

  const renewSubscription = async (userId, days = 30) => {
    try {
      await axios.put(`${API}/users/${userId}/renew?days=${days}`);
      // Reset to full mode when renewing
      await axios.post(`${API}/users/${userId}/set-full`);
      toast.success(`Suscripción renovada por ${days} días (acceso completo)`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al renovar suscripción');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('¿Seguro que quieres ELIMINAR permanentemente este usuario? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado permanentemente');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const getSubscriptionStatus = (user) => {
    if (user.role === 'admin') return { label: 'ADMIN', color: 'text-yellow-400 bg-yellow-500/20' };
    if (user.subscription_type === 'operativa') return { label: 'OPERATIVA', color: 'text-cyan-400 bg-cyan-500/20' };
    if (!user.subscription_expires_at) return { label: 'SIN SUSCRIPCIÓN', color: 'text-gray-400 bg-gray-500/20' };
    const exp = new Date(user.subscription_expires_at);
    const now = new Date();
    const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return { label: 'EXPIRADA', color: 'text-red-400 bg-red-500/20' };
    if (daysLeft <= 7) return { label: `${daysLeft}d restantes`, color: 'text-orange-400 bg-orange-500/20' };
    return { label: `${daysLeft}d restantes`, color: 'text-green-400 bg-green-500/20' };
  };

  const resetSignalForm = () => {
    setSignalForm({
      title: '',
      signal_type: 'buy',
      symbol: '',
      entry_price: '',
      stop_loss: '',
      take_profit: '',
      take_profits: [''],
      description: '',
      chart_image_base64: '',
      chart_image_file: null,
      status: 'active',
      close_price: ''
    });
    setEditMode(false);
    setCurrentId(null);
  };

  const resetVideoForm = () => {
    setVideoForm({ title: '', url: '', platform: 'youtube', category: 'basicos', description: '', order: 0 });
    setEditingVideoId(null);
  };

  const resetResourceForm = () => {
    setResourceForm({
      title: '',
      url: '',
      resource_type: 'pdf',
      description: '',
      uploadMode: 'url'
    });
  };

  const startEditSignal = (signal) => {
    const tps = signal.take_profits?.length > 0 
      ? signal.take_profits.map(tp => tp.toString()) 
      : (signal.take_profit ? [signal.take_profit.toString()] : ['']);
    setSignalForm({
      title: signal.title,
      signal_type: signal.signal_type,
      symbol: signal.symbol,
      entry_price: signal.entry_price.toString(),
      stop_loss: signal.stop_loss?.toString() || '',
      take_profit: signal.take_profit?.toString() || '',
      take_profits: tps,
      description: signal.description,
      chart_image_base64: signal.chart_image_url || '',
      status: signal.status || 'active',
      close_price: signal.close_price?.toString() || ''
    });
    setCurrentId(signal.id);
    setEditMode(true);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#020402]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-7xl">
          <h1 
            className="text-4xl font-bold mb-8 text-emerald-50"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
            data-testid="admin-title"
          >
            PANEL DE ADMINISTRACIÓN
          </h1>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-black/40 border border-emerald-500/20">
              <TabsTrigger value="posts" data-testid="tab-posts">Social</TabsTrigger>
              <TabsTrigger value="signals" data-testid="tab-signals">Señales</TabsTrigger>
              <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
              <TabsTrigger value="resources" data-testid="tab-resources">Recursos</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">Usuarios</TabsTrigger>
              <TabsTrigger value="alerts" data-testid="tab-alerts">Alertas</TabsTrigger>
            </TabsList>

            {/* POSTS TAB */}
            <TabsContent value="posts" className="space-y-4">
              <div className="glass-panel rounded-lg p-5 mb-4">
                <h3 className="text-lg font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  NUEVA PUBLICACIÓN
                </h3>
                <Textarea
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  placeholder="Escribe una actualización para la comunidad..."
                  rows={4}
                  className="bg-black/50 border-emerald-500/30 mb-3 resize-none"
                  data-testid="admin-post-textarea"
                />
                {postImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {postImages.map((img, i) => (
                      <div key={i} className="relative group">
                        <img src={img} alt="" className="h-16 w-16 object-cover rounded border border-emerald-500/30" />
                        <button onClick={() => setPostImages(postImages.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <label className="cursor-pointer text-emerald-400 hover:text-emerald-300 flex items-center space-x-2 text-sm">
                    <span>{postUploading ? 'Subiendo...' : 'Agregar imágenes'}</span>
                    <Input type="file" accept="image/*" multiple onChange={handlePostImageUpload} className="hidden" />
                  </label>
                  <Button onClick={createPost} disabled={!postContent.trim()} className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="admin-post-submit">
                    <Plus className="h-4 w-4 mr-2" /> Publicar
                  </Button>
                </div>
              </div>

              <div className="grid gap-4" data-testid="admin-posts-list">
                {/* Poll Creation */}
                <div className="glass-panel rounded-lg p-5">
                  <h3 className="text-lg font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                    NUEVA ENCUESTA
                  </h3>
                  <Input
                    value={pollForm.question}
                    onChange={(e) => setPollForm({ ...pollForm, question: e.target.value })}
                    placeholder="Escribe la pregunta de la encuesta..."
                    className="bg-black/50 border-emerald-500/30 mb-3"
                    data-testid="admin-poll-question"
                  />
                  <div className="space-y-2 mb-3">
                    {pollForm.options.map((opt, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <span className="text-emerald-400 text-xs font-bold w-6">{i + 1}.</span>
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const newOpts = [...pollForm.options];
                            newOpts[i] = e.target.value;
                            setPollForm({ ...pollForm, options: newOpts });
                          }}
                          placeholder={`Opción ${i + 1}`}
                          className="bg-black/50 border-emerald-500/30 flex-1"
                          data-testid={`admin-poll-option-${i}`}
                        />
                        {pollForm.options.length > 2 && (
                          <button onClick={() => setPollForm({ ...pollForm, options: pollForm.options.filter((_, idx) => idx !== i) })} className="text-red-400 hover:text-red-300 text-sm px-2">x</button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setPollForm({ ...pollForm, options: [...pollForm.options, ''] })} className="text-emerald-400 hover:text-emerald-300 text-sm" data-testid="admin-poll-add-option">+ Agregar opción</button>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-2 text-sm text-emerald-100/70 cursor-pointer">
                      <input type="checkbox" checked={pollForm.allow_multiple} onChange={(e) => setPollForm({ ...pollForm, allow_multiple: e.target.checked })} className="accent-emerald-500" data-testid="admin-poll-multiple" />
                      <span>Permitir múltiple selección</span>
                    </label>
                    <Button onClick={createPoll} className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="admin-poll-submit">
                      <Plus className="h-4 w-4 mr-2" /> Crear Encuesta
                    </Button>
                  </div>
                </div>

                {/* Polls List */}
                {polls.length > 0 && (
                  <div className="border-t border-emerald-500/20 pt-4">
                    <h4 className="text-sm font-bold text-emerald-400 mb-3">ENCUESTAS ACTIVAS</h4>
                    {polls.map((poll) => (
                      <div key={poll.id} className="glass-panel rounded-lg p-4 mb-3" data-testid={`admin-poll-${poll.id}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="text-emerald-50 font-bold mb-1">{poll.question}</p>
                            <div className="flex flex-wrap gap-2 text-xs text-emerald-100/60">
                              {poll.options?.map((opt) => (
                                <span key={opt.id} className="bg-black/30 border border-emerald-500/10 px-2 py-1 rounded">{opt.text}: {opt.votes}</span>
                              ))}
                            </div>
                            <p className="text-emerald-100/40 text-xs mt-1">{new Date(poll.created_at).toLocaleString('es-ES')}</p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => deletePoll(poll.id)} className="border-red-500/50 text-red-400 ml-3" data-testid={`admin-poll-delete-${poll.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Posts List */}
                {posts.map((post) => (
                  <div key={post.id} className="glass-panel rounded-lg p-4" data-testid={`admin-post-${post.id}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-emerald-50 font-bold mb-1">{post.author_name}</p>
                        <p className="text-emerald-100/80 whitespace-pre-wrap text-sm mb-2">{post.content.substring(0, 200)}{post.content.length > 200 ? '...' : ''}</p>
                        {post.images && post.images.length > 0 && (
                          <p className="text-emerald-400/60 text-xs">{post.images.length} imagen(es)</p>
                        )}
                        <p className="text-emerald-100/40 text-xs mt-1">{new Date(post.created_at).toLocaleString('es-ES')}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => deletePost(post.id)} className="border-red-500/50 text-red-400 ml-3" data-testid={`admin-post-delete-${post.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* SIGNALS TAB */}
            <TabsContent value="signals" className="space-y-4">
              <Dialog open={dialogOpen && activeTab === 'signals'} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetSignalForm(); }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-emerald-500 text-black hover:bg-emerald-400"
                    data-testid="add-signal-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva Señal
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#050a05] border-emerald-500/30 text-emerald-50 max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-2xl" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                      {editMode ? 'Editar Señal' : 'Nueva Señal'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input 
                        value={signalForm.title}
                        onChange={(e) => setSignalForm({ ...signalForm, title: e.target.value })}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="signal-title-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo</Label>
                        <Select value={signalForm.signal_type} onValueChange={(v) => setSignalForm({ ...signalForm, signal_type: v })}>
                          <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="signal-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#050a05] border-emerald-500/30">
                            <SelectItem value="buy">Compra</SelectItem>
                            <SelectItem value="sell">Venta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Símbolo</Label>
                        <Input 
                          value={signalForm.symbol}
                          onChange={(e) => setSignalForm({ ...signalForm, symbol: e.target.value })}
                          placeholder="BTC/USDT"
                          className="bg-black/50 border-emerald-500/30"
                          data-testid="signal-symbol-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Precio Entrada</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={signalForm.entry_price}
                          onChange={(e) => setSignalForm({ ...signalForm, entry_price: e.target.value })}
                          className="bg-black/50 border-emerald-500/30"
                          data-testid="signal-entry-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stop Loss</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={signalForm.stop_loss}
                          onChange={(e) => setSignalForm({ ...signalForm, stop_loss: e.target.value })}
                          className="bg-black/50 border-emerald-500/30"
                          data-testid="signal-stoploss-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Take Profits</Label>
                      <div className="space-y-2">
                        {signalForm.take_profits.map((tp, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <span className="text-emerald-400 text-xs font-bold w-8">TP{idx + 1}</span>
                            <Input
                              type="number"
                              step="0.01"
                              value={tp}
                              onChange={(e) => {
                                const newTps = [...signalForm.take_profits];
                                newTps[idx] = e.target.value;
                                setSignalForm({ ...signalForm, take_profits: newTps });
                              }}
                              placeholder={`Take Profit ${idx + 1}`}
                              className="bg-black/50 border-emerald-500/30 flex-1"
                              data-testid={`signal-tp-${idx}`}
                            />
                            {signalForm.take_profits.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const newTps = signalForm.take_profits.filter((_, i) => i !== idx);
                                  setSignalForm({ ...signalForm, take_profits: newTps });
                                }}
                                className="text-red-400 hover:text-red-300 text-sm px-2"
                              >x</button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setSignalForm({ ...signalForm, take_profits: [...signalForm.take_profits, ''] })}
                          className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center space-x-1"
                          data-testid="add-tp-btn"
                        >
                          <span>+ Agregar Take Profit</span>
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción / Análisis</Label>
                      <Textarea 
                        value={signalForm.description}
                        onChange={(e) => setSignalForm({ ...signalForm, description: e.target.value })}
                        rows={4}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="signal-description-input"
                      />
                    </div>
                    
                    {editMode && (
                      <>
                        <div className="border-t border-emerald-500/20 pt-4">
                          <h4 className="text-lg font-bold mb-3 text-emerald-400">Cerrar Operación</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Precio de Cierre</Label>
                              <Input 
                                type="number"
                                step="0.01"
                                value={signalForm.close_price || ''}
                                onChange={(e) => setSignalForm({ ...signalForm, close_price: e.target.value })}
                                placeholder="Ingrese precio final"
                                className="bg-black/50 border-emerald-500/30"
                                data-testid="signal-closeprice-input"
                              />
                              <p className="text-xs text-emerald-100/50">
                                Al ingresar el precio de cierre, se calculará automáticamente el resultado, pips y ganancia
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label>Estado</Label>
                              <Select value={signalForm.status} onValueChange={(v) => setSignalForm({ ...signalForm, status: v })}>
                                <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="signal-status-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#050a05] border-emerald-500/30">
                                  <SelectItem value="active">Activa</SelectItem>
                                  <SelectItem value="closed">Cerrada</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    <div className="space-y-2">
                      <Label>Imagen del Gráfico</Label>
                      <p className="text-xs text-emerald-100/50 mb-2">
                        Selecciona una imagen desde tu equipo
                      </p>
                      <Input 
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="signal-image-input"
                      />
                      {signalForm.chart_image_base64 && (
                        <img src={signalForm.chart_image_base64} alt="Preview" className="mt-2 w-full border border-emerald-500/20 rounded" />
                      )}
                    </div>
                    <Button 
                      onClick={editMode ? updateSignal : createSignal}
                      className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
                      data-testid="signal-save-btn"
                    >
                      {editMode ? 'Actualizar Señal' : 'Crear Señal'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="grid gap-4" data-testid="signals-list">
                {signals.map((signal) => (
                  <div key={signal.id} className="glass-panel rounded-lg p-4 flex justify-between items-center" data-testid={`signal-item-${signal.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-emerald-50">{signal.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded ${
                          signal.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {signal.status === 'active' ? 'ACTIVA' : 'CERRADA'}
                        </span>
                        {signal.result && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            signal.result === 'won' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {signal.result === 'won' ? '✓ GANADA' : '✗ PERDIDA'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <p className="text-emerald-400">{signal.symbol}</p>
                        <p className="text-emerald-100/70">${signal.entry_price}</p>
                        {signal.risk_reward_ratio && (
                          <p className="text-emerald-400">R/R: 1:{signal.risk_reward_ratio}</p>
                        )}
                        {signal.pips !== null && signal.pips !== undefined && (
                          <p className={signal.pips >= 0 ? 'text-green-400' : 'text-red-400'}>
                            {signal.pips > 0 ? '+' : ''}{signal.pips} pips
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => startEditSignal(signal)}
                        className="border-emerald-500/50 text-emerald-400"
                        data-testid={`signal-edit-${signal.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => deleteSignal(signal.id)}
                        className="border-red-500/50 text-red-400"
                        data-testid={`signal-delete-${signal.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* VIDEOS TAB */}
            <TabsContent value="videos" className="space-y-4">
              <Dialog open={dialogOpen && activeTab === 'videos'} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetVideoForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="add-video-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Video
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#050a05] border-emerald-500/30 text-emerald-50">
                  <DialogHeader>
                    <DialogTitle className="text-2xl" style={{ fontFamily: 'Rajdhani, sans-serif' }}>{editingVideoId ? 'Editar Video' : 'Nuevo Video'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input 
                        value={videoForm.title}
                        onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="video-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>URL</Label>
                      <Input 
                        value={videoForm.url}
                        onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })}
                        placeholder="https://youtube.com/watch?v=..."
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="video-url-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Plataforma</Label>
                        <Select value={videoForm.platform} onValueChange={(v) => setVideoForm({ ...videoForm, platform: v })}>
                          <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="video-platform-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#050a05] border-emerald-500/30">
                            <SelectItem value="youtube">YouTube</SelectItem>
                            <SelectItem value="vimeo">Vimeo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Categoría</Label>
                        <Select value={videoForm.category} onValueChange={(v) => setVideoForm({ ...videoForm, category: v })}>
                          <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="video-category-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#050a05] border-emerald-500/30">
                            <SelectItem value="shorts">Shorts</SelectItem>
                            <SelectItem value="basicos">Básicos</SelectItem>
                            <SelectItem value="avanzados">Avanzados</SelectItem>
                            <SelectItem value="psicotrading">Psicotrading</SelectItem>
                            <SelectItem value="algoritmos">Algoritmos</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Orden (menor = primero)</Label>
                      <Input
                        type="number"
                        value={videoForm.order}
                        onChange={(e) => setVideoForm({ ...videoForm, order: parseInt(e.target.value) || 0 })}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="video-order-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea 
                        value={videoForm.description}
                        onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
                        rows={2}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="video-description-input"
                      />
                    </div>
                    <Button 
                      onClick={createVideo}
                      className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
                      data-testid="video-save-btn"
                    >
                      {editingVideoId ? 'Guardar Cambios' : 'Agregar Video'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Category filter */}
              <div className="flex flex-wrap gap-2" data-testid="video-cat-filter">
                {[{ key: 'all', label: 'Todos' }, { key: 'shorts', label: 'Shorts' }, { key: 'basicos', label: 'Básicos' }, { key: 'avanzados', label: 'Avanzados' }, { key: 'psicotrading', label: 'Psicotrading' }, { key: 'algoritmos', label: 'Algoritmos' }].map(c => (
                  <button key={c.key} onClick={() => setVideoCategoryFilter(c.key)} className={`text-xs px-3 py-1.5 rounded-lg ${videoCategoryFilter === c.key ? 'bg-emerald-500 text-black font-bold' : 'bg-black/30 border border-emerald-500/20 text-emerald-100/70 hover:bg-emerald-500/10'}`}>
                    {c.label} ({c.key === 'all' ? videos.length : videos.filter(v => v.category === c.key).length})
                  </button>
                ))}
              </div>

              <div className="space-y-2" data-testid="videos-list">
                {videos
                  .filter(v => videoCategoryFilter === 'all' || v.category === videoCategoryFilter)
                  .sort((a, b) => (a.category || '').localeCompare(b.category || '') || (a.order || 0) - (b.order || 0))
                  .map((video, idx, arr) => (
                  <div key={video.id} className="glass-panel rounded-lg p-3 flex items-center gap-3" data-testid={`video-item-${video.id}`}>
                    {/* Order arrows */}
                    <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                      <button onClick={() => moveVideo(video, -1)} className="text-emerald-100/30 hover:text-emerald-400 disabled:opacity-20" disabled={idx === 0 || (idx > 0 && arr[idx-1]?.category !== video.category)} data-testid={`video-up-${video.id}`}>
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <span className="text-emerald-100/30 text-[10px] font-mono">{video.order || 0}</span>
                      <button onClick={() => moveVideo(video, 1)} className="text-emerald-100/30 hover:text-emerald-400 disabled:opacity-20" disabled={idx === arr.length - 1 || (idx < arr.length - 1 && arr[idx+1]?.category !== video.category)} data-testid={`video-down-${video.id}`}>
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-emerald-50 truncate">{video.title}</h3>
                      <div className="flex items-center space-x-2 text-xs mt-0.5">
                        <span className="text-emerald-400/70">{video.platform}</span>
                        <span className="text-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 rounded text-[10px] uppercase">{video.category || 'basicos'}</span>
                        {video.description && <span className="text-emerald-100/30 truncate">{video.description.slice(0, 40)}</span>}
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button size="sm" variant="outline" onClick={() => startEditVideo(video)} className="border-emerald-500/30 text-emerald-400 h-8 px-2" data-testid={`video-edit-${video.id}`}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteVideo(video.id)} className="border-red-500/50 text-red-400 h-8 px-2" data-testid={`video-delete-${video.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* RESOURCES TAB */}
            <TabsContent value="resources" className="space-y-4">
              <Dialog open={dialogOpen && activeTab === 'resources'} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetResourceForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="add-resource-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Recurso
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#050a05] border-emerald-500/30 text-emerald-50">
                  <DialogHeader>
                    <DialogTitle className="text-2xl" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Nuevo Recurso</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input 
                        value={resourceForm.title}
                        onChange={(e) => setResourceForm({ ...resourceForm, title: e.target.value })}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="resource-title-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Método de Carga</Label>
                      <div className="flex space-x-4">
                        <button
                          type="button"
                          onClick={() => setResourceForm({ ...resourceForm, uploadMode: 'url', url: '' })}
                          className={`flex-1 py-2 px-4 rounded border ${
                            resourceForm.uploadMode === 'url'
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                              : 'bg-black/50 border-emerald-500/30 text-emerald-100/70'
                          }`}
                        >
                          URL / Enlace
                        </button>
                        <button
                          type="button"
                          onClick={() => setResourceForm({ ...resourceForm, uploadMode: 'file', url: '' })}
                          className={`flex-1 py-2 px-4 rounded border ${
                            resourceForm.uploadMode === 'file'
                              ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                              : 'bg-black/50 border-emerald-500/30 text-emerald-100/70'
                          }`}
                        >
                          Subir Archivo
                        </button>
                      </div>
                    </div>
                    
                    {resourceForm.uploadMode === 'url' ? (
                      <div className="space-y-2">
                        <Label>URL del Recurso</Label>
                        <Input 
                          value={resourceForm.url}
                          onChange={(e) => setResourceForm({ ...resourceForm, url: e.target.value })}
                          placeholder="https://ejemplo.com/documento.pdf"
                          className="bg-black/50 border-emerald-500/30"
                          data-testid="resource-url-input"
                        />
                        <p className="text-xs text-emerald-100/50">
                          Puedes usar enlaces de Google Drive, Dropbox, OneDrive, etc.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label>Subir Archivo</Label>
                        <Input 
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                          onChange={handleResourceFileUpload}
                          className="bg-black/50 border-emerald-500/30"
                          data-testid="resource-file-input"
                        />
                        {resourceForm.url && !resourceForm.url.startsWith('data:') && resourceForm.uploadMode === 'file' && (
                          <p className="text-xs text-green-400 mt-2">Archivo subido correctamente</p>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select value={resourceForm.resource_type} onValueChange={(v) => setResourceForm({ ...resourceForm, resource_type: v })}>
                        <SelectTrigger className="bg-black/50 border-emerald-500/30" data-testid="resource-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#050a05] border-emerald-500/30">
                          <SelectItem value="pdf">PDF</SelectItem>
                          <SelectItem value="document">Documento</SelectItem>
                          <SelectItem value="link">Enlace</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción</Label>
                      <Textarea 
                        value={resourceForm.description}
                        onChange={(e) => setResourceForm({ ...resourceForm, description: e.target.value })}
                        rows={3}
                        className="bg-black/50 border-emerald-500/30"
                        data-testid="resource-description-input"
                      />
                    </div>
                    
                    {/* Help section */}
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                      <p className="text-sm text-emerald-400 font-semibold mb-2">💡 Cómo compartir archivos desde Google Drive:</p>
                      <ol className="text-xs text-emerald-100/70 space-y-1 list-decimal list-inside">
                        <li>Sube tu archivo a Google Drive</li>
                        <li>Clic derecho en el archivo → "Obtener enlace"</li>
                        <li>Cambia permisos a "Cualquiera con el enlace"</li>
                        <li>Copia el enlace y pégalo arriba</li>
                      </ol>
                    </div>
                    
                    <Button 
                      onClick={createResource}
                      className="w-full bg-emerald-500 text-black hover:bg-emerald-400"
                      data-testid="resource-save-btn"
                    >
                      Agregar Recurso
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="grid gap-4" data-testid="resources-list">
                {resources.map((resource) => (
                  <div key={resource.id} className="glass-panel rounded-lg p-4 flex justify-between items-center" data-testid={`resource-item-${resource.id}`}>
                    <div>
                      <h3 className="text-xl font-bold text-emerald-50">{resource.title}</h3>
                      <p className="text-emerald-400/70 text-sm">{resource.resource_type}</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => deleteResource(resource.id)}
                      className="border-red-500/50 text-red-400"
                      data-testid={`resource-delete-${resource.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* USERS TAB */}
            <TabsContent value="users" className="space-y-4">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-emerald-50 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  GESTIÓN DE USUARIOS Y SUSCRIPCIONES
                </h2>
                <p className="text-emerald-100/70 text-sm">
                  Administra el acceso y las suscripciones de los usuarios
                </p>
              </div>

              <div className="grid gap-4" data-testid="users-list">
                {users.map((user) => {
                  const subStatus = getSubscriptionStatus(user);
                  return (
                    <div key={user.id} className="glass-panel rounded-lg p-4" data-testid={`user-item-${user.id}`}>
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2 flex-wrap gap-y-1">
                            <h3 className="text-lg font-bold text-emerald-50">{user.name}</h3>
                            <span className={`text-xs px-2 py-1 rounded ${
                              user.role === 'admin' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'
                            }`}>
                              {user.role === 'admin' ? 'ADMIN' : 'USUARIO'}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              user.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {user.active ? 'ACTIVO' : 'DESACTIVADO'}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${subStatus.color}`}>
                              {subStatus.label}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-emerald-100/70 flex-wrap gap-y-1">
                            <p>{user.email}</p>
                            <p>Registrado: {new Date(user.created_at).toLocaleDateString('es-ES')}</p>
                            {user.subscription_expires_at && (
                              <p>Vence: {new Date(user.subscription_expires_at).toLocaleDateString('es-ES')}</p>
                            )}
                          </div>
                        </div>
                        {user.role !== 'admin' && (
                          <div className="flex flex-wrap gap-2 ml-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/users/${user.id}/set-operativa`);
                                  toast.success('Modo Operativa activado (solo EN VIVO)');
                                  fetchAll();
                                } catch { toast.error('Error'); }
                              }}
                              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                              data-testid={`user-operativa-${user.id}`}
                            >
                              Operativa
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => renewSubscription(user.id, 15)}
                              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                              data-testid={`user-renew-trial-${user.id}`}
                            >
                              +15 Días
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => renewSubscription(user.id, 30)}
                              className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                              data-testid={`user-renew-${user.id}`}
                            >
                              <RefreshCw className="h-4 w-4 mr-1" />
                              +1 Mes
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => renewSubscription(user.id, 180)}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                              data-testid={`user-renew-semester-${user.id}`}
                            >
                              +6 Meses
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => renewSubscription(user.id, 365)}
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                              data-testid={`user-renew-annual-${user.id}`}
                            >
                              +1 Año
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => toggleUserAccess(user.id, user.active)}
                              className={`${
                                user.active 
                                  ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' 
                                  : 'border-green-500/50 text-green-400 hover:bg-green-500/10'
                              }`}
                              data-testid={`user-toggle-${user.id}`}
                            >
                              {user.active ? 'Desactivar' : 'Activar'}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => deleteUser(user.id)}
                              className="border-red-700/50 text-red-500 hover:bg-red-700/10"
                              data-testid={`user-delete-${user.id}`}
                            >
                              <UserX className="h-4 w-4 mr-1" />
                              Eliminar
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </TabsContent>

            {/* ALERTS TAB */}
            <TabsContent value="alerts" className="space-y-4">
              <div className="glass-panel rounded-lg p-5 mb-4">
                <h3 className="text-lg font-bold text-emerald-50 mb-3" style={{ fontFamily: 'Rajdhani, sans-serif' }}>NUEVA ALERTA DE VOLATILIDAD</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div><Label className="text-xs">Título</Label><Input value={alertForm.title} onChange={(e) => setAlertForm({...alertForm, title: e.target.value})} placeholder="Habla Powell - Fed" className="bg-black/50 border-emerald-500/30" data-testid="admin-alert-title" /></div>
                  <div><Label className="text-xs">Hora del evento</Label><Input type="datetime-local" value={alertForm.event_time} onChange={(e) => setAlertForm({...alertForm, event_time: e.target.value})} className="bg-black/50 border-emerald-500/30" data-testid="admin-alert-time" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div><Label className="text-xs">Descripción</Label><Textarea value={alertForm.description} onChange={(e) => setAlertForm({...alertForm, description: e.target.value})} placeholder="Alta volatilidad esperada..." rows={2} className="bg-black/50 border-emerald-500/30" data-testid="admin-alert-desc" /></div>
                  <div><Label className="text-xs">Impacto</Label>
                    <Select value={alertForm.impact} onValueChange={(v) => setAlertForm({...alertForm, impact: v})}>
                      <SelectTrigger className="bg-black/50 border-emerald-500/30"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#050a05] border-emerald-500/30"><SelectItem value="high">Alto</SelectItem><SelectItem value="medium">Medio</SelectItem><SelectItem value="low">Bajo</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={createAlert} className="bg-emerald-500 text-black hover:bg-emerald-400" data-testid="admin-alert-submit"><Plus className="h-4 w-4 mr-2" />Crear Alerta</Button>
              </div>
              <div className="grid gap-3" data-testid="admin-alerts-list">
                {alerts.map((a) => (
                  <div key={a.id} className="glass-panel rounded-lg p-4 flex justify-between items-center">
                    <div>
                      <p className="text-emerald-50 font-bold">{a.title}</p>
                      <p className="text-emerald-100/60 text-sm">{a.description}</p>
                      <p className="text-emerald-100/40 text-xs">{new Date(a.event_time).toLocaleString('es-CO', {timeZone:'America/Bogota'})} - Impacto: {a.impact}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => deleteAlert(a.id)} className="border-red-500/50 text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}